import React from 'react';
import Tooltip, { TooltipPlacement } from '../Tooltip';

import style from './style.module.scss';

require('@material/web/ripple/ripple.js');
require('@material/web/focus/md-focus-ring.js');
require('@material/web/elevation/elevation.js');
require('@material/web/button/elevated-button.js');
require('@material/web/button/filled-button.js');
require('@material/web/button/filled-tonal-button.js');
require('@material/web/button/outlined-button.js');
require('@material/web/button/text-button.js');
require('@material/web/iconbutton/icon-button.js');
require('@material/web/iconbutton/filled-icon-button.js');
require('@material/web/iconbutton/filled-tonal-icon-button.js');
require('@material/web/iconbutton/outlined-icon-button.js');

export type ButtonVariant =
  | 'elevated'
  | 'filled'
  | 'tonal'
  | 'outlined'
  | 'text';

export type ButtonShape = 'round' | 'square';

export type ButtonSize =
  | 'extra-small'
  | 'small'
  | 'medium'
  | 'large'
  | 'extra-large';

export interface ButtonProps extends React.HTMLAttributes<HTMLElement> {
  children?: React.ReactNode;
  variant?: ButtonVariant;
  shape?: ButtonShape;
  size?: ButtonSize;
  icon?: React.ReactElement<any>;
  trailingIcon?: React.ReactElement<any>;
  disabled?: boolean;
  softDisabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  name?: string;
  value?: string;
  href?: string;
  target?: '_blank' | '_parent' | '_self' | '_top' | '';
  download?: string;
  fullWidth?: boolean;
  iconButton?: boolean;
  tooltip?: React.ReactNode;
  tooltipPlacement?: TooltipPlacement;
}

const variantElements: { [key in ButtonVariant]: string } = {
  elevated: 'md-elevated-button',
  filled: 'md-filled-button',
  tonal: 'md-filled-tonal-button',
  outlined: 'md-outlined-button',
  text: 'md-text-button',
};

const iconButtonElements: { [key in ButtonVariant]: string } = {
  elevated: 'md-icon-button',
  filled: 'md-filled-icon-button',
  tonal: 'md-filled-tonal-icon-button',
  outlined: 'md-outlined-icon-button',
  text: 'md-icon-button',
};

const variantClasses: { [key in ButtonVariant]: string } = {
  elevated: style.buttonElevated,
  filled: style.buttonFilled,
  tonal: style.buttonTonal,
  outlined: style.buttonOutlined,
  text: style.buttonText,
};

const shapeClasses: { [key in ButtonShape]: string } = {
  round: style.buttonRound,
  square: style.buttonSquare,
};

const sizeClasses: { [key in ButtonSize]: string } = {
  'extra-small': style.buttonExtraSmall,
  small: style.buttonSmall,
  medium: style.buttonMedium,
  large: style.buttonLarge,
  'extra-large': style.buttonExtraLarge,
};

const iconButtonSizeClasses: { [key in ButtonSize]: string } = {
  'extra-small': style.iconButtonExtraSmall,
  small: style.iconButtonSmall,
  medium: style.iconButtonMedium,
  large: style.iconButtonLarge,
  'extra-large': style.iconButtonExtraLarge,
};

interface CommonMaterialProperties {
  attributes: React.HTMLAttributes<HTMLElement>;
  classes: string;
  disabled: boolean;
  softDisabled: boolean;
  href?: string;
  target?: ButtonProps['target'];
  download?: string;
}

function createMaterialProperties({
  attributes,
  classes,
  disabled,
  softDisabled,
  href,
  target,
  download,
}: CommonMaterialProperties): { [key: string]: any } {
  const properties: { [key: string]: any } = {
    ...attributes,
    class: classes,
  };

  if (disabled) {
    properties.disabled = '';
  }

  if (softDisabled) {
    properties['soft-disabled'] = '';
  }

  if (href !== undefined) {
    properties.href = href;
  }

  if (target !== undefined) {
    properties.target = target;
  }

  if (download !== undefined) {
    properties.download = download;
  }

  return properties;
}

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'filled',
  shape = 'round',
  size = 'small',
  icon,
  trailingIcon,
  disabled = false,
  softDisabled = false,
  type = 'button',
  name,
  value,
  href,
  target,
  download,
  fullWidth = false,
  iconButton = false,
  tooltip,
  tooltipPlacement,
  className = '',
  ...htmlAttributes
}) => {
  if (iconButton && icon) {
    const iconButtonClasses = [
      style.iconButton,
      iconButtonSizeClasses[size],
      className,
    ]
      .filter(Boolean)
      .join(' ');
    const iconButtonProperties = createMaterialProperties({
      attributes: htmlAttributes,
      classes: iconButtonClasses,
      disabled,
      softDisabled,
      href,
      target,
      download,
    });
    iconButtonProperties.type = type;

    if (name !== undefined) {
      iconButtonProperties.name = name;
    }

    if (value !== undefined) {
      iconButtonProperties.value = value;
    }

    const iconButtonIcon = React.cloneElement(icon, {
      key: 'icon',
      className: [style.iconButtonIcon, icon.props.className]
        .filter(Boolean)
        .join(' '),
      'aria-hidden': icon.props['aria-label'] ? undefined : true,
    });

    const iconButtonElement = React.createElement(
      iconButtonElements[variant],
      iconButtonProperties,
      iconButtonIcon,
    );

    return tooltip ? (
      <Tooltip text={tooltip} placement={tooltipPlacement}>
        {iconButtonElement}
      </Tooltip>
    ) : iconButtonElement;
  }

  const elementName = variantElements[variant];
  const classes = [
    style.button,
    variantClasses[variant],
    shapeClasses[shape],
    sizeClasses[size],
    fullWidth ? style.buttonFullWidth : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const materialProperties = createMaterialProperties({
    attributes: htmlAttributes,
    classes,
    disabled,
    softDisabled,
    href,
    target,
    download,
  });
  materialProperties.type = type;

  if (icon || trailingIcon) {
    materialProperties['has-icon'] = '';
  }

  const useNativeTrailingIcon = Boolean(trailingIcon && !icon && !href);

  if (useNativeTrailingIcon) {
    materialProperties['trailing-icon'] = '';
  }

  if (name !== undefined) {
    materialProperties.name = name;
  }

  if (value !== undefined) {
    materialProperties.value = value;
  }

  const slottedLeadingIcon = icon
    ? React.cloneElement(icon, {
        key: 'leading-icon',
        slot: 'icon',
        className: [style.buttonIcon, icon.props.className]
          .filter(Boolean)
          .join(' '),
        'aria-hidden': icon.props['aria-label'] ? undefined : true,
      })
    : null;
  const slottedTrailingIcon = trailingIcon
    ? React.cloneElement(trailingIcon, {
        key: 'trailing-icon',
        slot: useNativeTrailingIcon ? 'icon' : undefined,
        className: [
          style.buttonIcon,
          useNativeTrailingIcon ? '' : style.buttonTrailingIcon,
          trailingIcon.props.className,
        ]
          .filter(Boolean)
          .join(' '),
        'aria-hidden': trailingIcon.props['aria-label']
          ? undefined
          : true,
      })
    : null;

  return React.createElement(
    elementName,
    materialProperties,
    slottedLeadingIcon,
    children,
    slottedTrailingIcon,
  );
};

export default Button;
